import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const OfflineContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'pollo_pos_offline_data';

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);
  const [syncStatus, setSyncStatus] = useState(null); // null | 'syncing' | 'success' | 'error'
  const [lastSyncMessage, setLastSyncMessage] = useState('');
  const syncingRef = useRef(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when reconnecting
      autoSync();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    updatePendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const updatePendingCount = () => {
    const data = getOfflineData();
    const count =
      data.asistencias.length +
      data.inventarios.length +
      data.movimientos.length +
      data.cortes.length +
      data.transferencias.length;
    setPendingSync(count);
    return count;
  };

  const getOfflineData = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch (e) {
      console.error('Error reading offline data:', e);
    }
    return { asistencias: [], inventarios: [], movimientos: [], cortes: [], transferencias: [] };
  };

  const saveOfflineData = (data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      updatePendingCount();
    } catch (e) {
      console.error('Error saving offline data:', e);
    }
  };

  const addOfflineItem = (type, item) => {
    const data = getOfflineData();
    item.offline_id = Date.now().toString();
    item.created_at = new Date().toISOString();
    data[type].push(item);
    saveOfflineData(data);
    return item;
  };

  const syncData = useCallback(async () => {
    if (!navigator.onLine || syncingRef.current) return { message: 'No disponible' };
    syncingRef.current = true;

    const data = getOfflineData();
    const count =
      data.asistencias.length +
      data.inventarios.length +
      data.movimientos.length +
      data.cortes.length +
      data.transferencias.length;

    if (count === 0) {
      syncingRef.current = false;
      return { message: 'No hay datos pendientes' };
    }

    setSyncStatus('syncing');
    setLastSyncMessage(`Sincronizando ${count} operaciones...`);

    try {
      const { data: result } = await axios.post(`${API}/sync`, data);
      // Clear synced data, keep only failed items
      const failedIds = new Set((result.errors || []).map(e => e.offline_id));

      if (failedIds.size > 0) {
        const newData = { asistencias: [], inventarios: [], movimientos: [], cortes: [], transferencias: [] };
        for (const type of Object.keys(newData)) {
          newData[type] = data[type].filter(item => failedIds.has(item.offline_id));
        }
        saveOfflineData(newData);
        const syncedCount = result.synced?.length || 0;
        setLastSyncMessage(`${syncedCount} sincronizados, ${failedIds.size} con error`);
        setSyncStatus('error');
      } else {
        saveOfflineData({ asistencias: [], inventarios: [], movimientos: [], cortes: [], transferencias: [] });
        const syncedCount = result.synced?.length || 0;
        setLastSyncMessage(`${syncedCount} operaciones sincronizadas`);
        setSyncStatus('success');
      }

      // Auto-clear status after 4 seconds
      setTimeout(() => {
        setSyncStatus(null);
        setLastSyncMessage('');
      }, 4000);

      syncingRef.current = false;
      return result;
    } catch (error) {
      setSyncStatus('error');
      setLastSyncMessage('Error de conexion al sincronizar');
      setTimeout(() => {
        setSyncStatus(null);
        setLastSyncMessage('');
      }, 4000);
      syncingRef.current = false;
      throw error;
    }
  }, []);

  const autoSync = useCallback(async () => {
    try {
      const count = updatePendingCount();
      if (count > 0) await syncData();
    } catch (e) {
      // silent fail for auto-sync
    }
  }, [syncData]);

  // Cache for offline reading
  const cacheData = (key, data) => {
    try {
      localStorage.setItem(`cache_${key}`, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (e) {
      console.error('Error caching data:', e);
    }
  };

  const getCachedData = (key, maxAge = 86400000) => { // 24 hours default
    try {
      const stored = localStorage.getItem(`cache_${key}`);
      if (stored) {
        const { data, timestamp } = JSON.parse(stored);
        if (Date.now() - timestamp < maxAge) return data;
      }
    } catch (e) {
      console.error('Error reading cached data:', e);
    }
    return null;
  };

  const value = {
    isOnline,
    pendingSync,
    syncStatus,
    lastSyncMessage,
    addOfflineItem,
    syncData,
    cacheData,
    getCachedData,
    getOfflineData,
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
}
